import {ChangeDetectorRef, Component, OnInit, Optional} from '@angular/core';
import {DATE_FORMAT_YMD_HM, Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {PermissionService} from '@ngmedax/permission';
import {languages} from '@ngmedax/cloud-translator';

import {MasterDataLayoutService, MasterDataLayout, MasterDataFieldPosition, CustomFieldDefinition} from '@ngmedax/master-data';

import {PatientService} from '../services/patient.service';
import {TRANSLATION_GRID_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {Patient} from '../../../types';

/**
 * Column configuration interface.
 */
export interface GridColumn {
  key: string;
  labelKey: string;
  sortField?: string;
  visible: boolean;
  isCustom?: boolean;
  customLabel?: string;
}

/**
 * LocalStorage key for column settings (visibility and order).
 */
const COLUMN_SETTINGS_STORAGE_KEY = 'patient-grid-columns';

/**
 * Keys of footer columns (always at the end).
 */
const FOOTER_COLUMN_KEYS = ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'];

/**
 * Default base columns configuration.
 */
const DEFAULT_BASE_COLUMNS: GridColumn[] = [
  {key: 'uid', labelKey: 'ID', sortField: undefined, visible: true},
  {key: 'name', labelKey: 'NAME', sortField: 'lastName', visible: true},
  {key: 'address', labelKey: 'ADDRESS', sortField: 'city', visible: true},
  {key: 'location', labelKey: 'LOCATION', sortField: 'location', visible: true},
  {key: 'customerNr', labelKey: 'PATIENT_NR', sortField: 'customerNr', visible: false},
  {key: 'email', labelKey: 'EMAIL', sortField: 'email', visible: false}
];

/**
 * Default footer columns configuration.
 */
const DEFAULT_FOOTER_COLUMNS: GridColumn[] = [
  {key: 'createdAt', labelKey: 'CREATED_AT', sortField: 'createdAt', visible: true},
  {key: 'updatedAt', labelKey: 'UPDATED_AT', sortField: 'updatedAt', visible: false},
  {key: 'createdBy', labelKey: 'CREATED_BY', sortField: 'createdBy', visible: false},
  {key: 'updatedBy', labelKey: 'UPDATED_BY', sortField: 'updatedBy', visible: true}
];

// hack to inject decorator declarations. must occur before class declaration!
export interface PatientGridComponent extends Translatable {}

@Component({
  selector: 'app-patient-grid',
  templateUrl: './patient-grid.component.html',
  styleUrls: ['./patient-grid.component.css'],
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class PatientGridComponent implements OnInit {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public isColumnSelectorOpen = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public patients: Patient[] = [];
  public filter: Patient.Filter = {};
  public total = 0;
  public sort = 'createdAt';
  public order: 'asc' | 'desc' = 'desc';


  /**
   * All columns including custom fields.
   */
  public columns: GridColumn[] = [];

  /**
   * Custom field definitions for search.
   */
  public customFieldDefinitions: CustomFieldDefinition[] = [];

  /**
   * Master data layout configuration (raw from API).
   */
  private masterDataLayout: MasterDataLayout | null = null;

  /**
   * Effective layout (custom or default).
   */
  public effectiveLayout: MasterDataLayout = {gridSize: {cols: 5, rows: 5}, positions: []};

  /**
   * Searchable field positions from the effective layout.
   */
  public searchablePositions: MasterDataFieldPosition[] = [];

  /**
   * Filter values for custom fields.
   */
  public customFilter: {[key: string]: string} = {};

  /**
   * Meta filter for type and uid (always visible).
   */
  public metaFilter: {type: string, uid: string} = {type: '', uid: ''};

  /**
   * Locale for patients. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi lang support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Available languages.
   */
  public languages = languages;

  /**
   * Timeout for filter change event
   */
  private filterChangeTimeout: any = null;

  /**
   * Timeout for show preloader event
   */
  private showPreloaderTimeout: any = null;

  /**
   * date format to use in grid
   * @type {string}
   */
  /**
   * date format to use in grid
   * @type {string}
   */
  public get dateFormat() {
    const format = DATE_FORMAT_YMD_HM.replace(/YYYY/, 'YY');
    return this.getDateFormat(format);
  };

  /**
   * Loads patients and initializes search filter.
   */
  public async ngOnInit() {
    const disablePatientNewLink = !(this.configService.get('feature.patient.new') == true);

    this.layoutService.showPreloader();
    this.filter = {};
    this.metaFilter = {
      type: disablePatientNewLink ? '' : '1',
      uid: ''
    };

    // check permission to edit settings
    if (this.permissionService) {
      this.canEditSettings = this.permissionService.isAllowed('MYMEDAX_SETTING_CREATE')
        && this.permissionService.isAllowed('MYMEDAX_SETTING_UPDATE');
    }

    // load grid settings default from API
    try {
      this.gridSettingsDefault = await this.patientService.loadGridColumnSettings();
    } catch (e) {
      console.error('Failed to load grid settings default', e);
    }

    // load master data layout and custom field definitions, then build columns
    await this.loadMasterDataLayout();
    await this.buildColumns();
    this.loadColumnSettings();
    this.loadPatients();
  }

  /**
   * Refreshes the patient grid data
   */
  public async onRefresh() {
    this.showPreloader();
    await new Promise(resolve => setTimeout(resolve, 500));
    this.loadPatients();
  }

  /**
   * Loads master data layout configuration from API and builds searchable positions.
   */
  private async loadMasterDataLayout() {
    try {
      this.masterDataLayout = await this.masterDataLayoutService.loadLayout();
    } catch (e) {
      console.error('Failed to load master data layout', e);
      this.masterDataLayout = null;
    }
    this.effectiveLayout = this.masterDataLayoutService.getEffectiveLayout(this.masterDataLayout);
    this.searchablePositions = this.masterDataLayoutService.getSearchableFields(this.effectiveLayout);
  }

  /**
   * Builds columns array including custom fields from API.
   * Creates fresh copies of default columns to ensure clean state.
   */
  private async buildColumns() {
    this.customFieldDefinitions = await this.patientService.loadCustomFieldDefinitions();
    const customColumns: GridColumn[] = this.customFieldDefinitions.map(def => ({
      key: `custom.${def.key}`,
      labelKey: def.key,
      customLabel: def.label,
      sortField: `custom.${def.key}`,
      visible: false,
      isCustom: true
    }));

    // create fresh copies of default columns
    const baseColumns = DEFAULT_BASE_COLUMNS.map(col => ({...col}));
    const footerColumns = DEFAULT_FOOTER_COLUMNS.map(col => ({...col}));

    this.columns = [...baseColumns, ...customColumns, ...footerColumns];
  }

  /**
   * Whether user can edit settings (save/reset grid defaults for all).
   */
  public canEditSettings = false;

  /**
   * Grid settings default from Settings API.
   */
  private gridSettingsDefault: {visibility: {[key: string]: boolean}, order: string[], sort: string, sortOrder: 'asc' | 'desc'} | null = null;

  /**
   * Injects dependencies.
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private permissionService: PermissionService,
    private configService: ConfigService,
    private layoutService: LayoutService,
    private patientService: PatientService,
    private masterDataLayoutService: MasterDataLayoutService,
    private ref: ChangeDetectorRef) {
  }

  public async onDeletePatient(patient: Patient) {
    const sure = await confirm(this._(KEYS.GRID.DELETE_PATIENT_QUESTION) + `: ${patient.firstName} ${patient.lastName}`);

    if (sure) {
      this.patientService
        .deletePatient(patient)
        .then(() => {
          this.patients.splice(this.patients.indexOf(patient), 1);
          alert(this._(KEYS.GRID.SUCESSFULLY_DELETED_PATIENT));
        })
        .catch(error => {
          console.error(error);
          alert(this._(KEYS.GRID.ERROR_DELETING_PATIENT));
        });
    }
  }

  /**
   * Event handler for when paging changes. Triggers change detection and loads patient
   */
  public onPagingChange() {
    // trigger change detection
    this.ref.detectChanges();
    this.ref.markForCheck();

    this.loadPatients();
  }

  /**
   * Event handler for when filter changes. Loads patients
   */
  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => this.loadPatients(), 1000);
  }

  /**
   * Changes sort order on column click.
   *
   * @param col Column name to sort by.
   */
  public onSortChange(col: string) {
    if (this.sort === col) {
      this.order = this.order === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort = col;
      this.order = 'asc';
    }
    this.saveColumnSettings();
    this.loadPatients();
  }

  /**
   * Checks if a column is visible.
   *
   * @param key Column key.
   * @returns true if visible.
   */
  public isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : false;
  }

  /**
   * Toggles column visibility.
   *
   * @param column Column to toggle.
   */
  public onToggleColumn(column: GridColumn) {
    column.visible = !column.visible;
    this.saveColumnSettings();
  }

  /**
   * Toggles the column selector dropdown.
   */
  public onToggleColumnSelector() {
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
  }

  /**
   * Checks if column is a footer column (not reorderable).
   */
  public isFooterColumn(col: GridColumn): boolean {
    return FOOTER_COLUMN_KEYS.indexOf(col.key) !== -1;
  }

  /**
   * Moves column up in the order.
   */
  public onMoveColumnUp(col: GridColumn) {
    const idx = this.columns.indexOf(col);
    if (idx <= 0 || this.isFooterColumn(col)) {
      return;
    }
    // don't move above another non-footer column
    const prevCol = this.columns[idx - 1];
    if (this.isFooterColumn(prevCol)) {
      return;
    }
    this.columns.splice(idx, 1);
    this.columns.splice(idx - 1, 0, col);
    this.saveColumnSettings();
  }

  /**
   * Moves column down in the order.
   */
  public onMoveColumnDown(col: GridColumn) {
    const idx = this.columns.indexOf(col);
    if (idx < 0 || this.isFooterColumn(col)) {
      return;
    }
    // don't move into footer columns
    const nextCol = this.columns[idx + 1];
    if (!nextCol || this.isFooterColumn(nextCol)) {
      return;
    }
    this.columns.splice(idx, 1);
    this.columns.splice(idx + 1, 0, col);
    this.saveColumnSettings();
  }

  /**
   * Resets column settings to default (removes localStorage entry and rebuilds columns).
   */
  public async onResetColumns() {
    try {
      localStorage.removeItem(COLUMN_SETTINGS_STORAGE_KEY);
    } catch (e) {
      console.error('Failed to remove column settings from localStorage', e);
    }
    // rebuild columns with default settings
    await this.buildColumns();

    // apply API default or layout-based visibility
    if (this.gridSettingsDefault) {
      this.applySettings(this.gridSettingsDefault);
    } else {
      this.applyLayoutBasedVisibility();
    }

    // trigger change detection
    this.ref.detectChanges();
  }

  /**
   * Saves current column settings as default for all users (Settings API).
   */
  public async onSaveAsDefault() {
    const settings = {
      visibility: <{[key: string]: boolean}>{},
      order: <string[]>[],
      sort: this.sort,
      sortOrder: this.order
    };

    this.columns.forEach(col => {
      settings.visibility[col.key] = col.visible;
      if (!this.isFooterColumn(col)) {
        settings.order.push(col.key);
      }
    });

    try {
      await this.patientService.saveSettingByKey('patientGrid.defaultColumns', settings);
      this.gridSettingsDefault = settings;
      alert(this._(KEYS.GRID.SUCCESSFULLY_SAVED_DEFAULT));
    } catch (e) {
      console.error('Failed to save grid settings default', e);
      alert(this._(KEYS.GRID.ERROR_SAVING_DEFAULT));
    }
  }

  /**
   * Resets default column settings for all users (removes from Settings API).
   * Also clears localStorage so user immediately sees layout-based defaults.
   */
  public async onResetDefault() {
    try {
      await this.patientService.deleteSettingByKey('patientGrid.defaultColumns');
      this.gridSettingsDefault = null;

      // also clear localStorage and rebuild columns with layout defaults
      try {
        localStorage.removeItem(COLUMN_SETTINGS_STORAGE_KEY);
      } catch (e) {
        console.error('Failed to remove column settings from localStorage', e);
      }

      await this.buildColumns();
      this.applyLayoutBasedVisibility();
      this.ref.detectChanges();

      alert(this._(KEYS.GRID.SUCCESSFULLY_RESET_DEFAULT));
    } catch (e) {
      console.error('Failed to reset grid settings default', e);
      alert(this._(KEYS.GRID.ERROR_SAVING_DEFAULT));
    }
  }

  /**
   * Saves column settings (visibility, order, sort) to localStorage.
   */
  private saveColumnSettings() {
    try {
      const settings = {
        visibility: <{[key: string]: boolean}>{},
        order: <string[]>[],
        sort: this.sort,
        sortOrder: this.order
      };
      this.columns.forEach(col => {
        settings.visibility[col.key] = col.visible;
        if (!this.isFooterColumn(col)) {
          settings.order.push(col.key);
        }
      });
      localStorage.setItem(COLUMN_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to save column settings', e);
    }
  }

  /**
   * Loads column settings (visibility and order) from localStorage.
   * Falls back to Settings API default, then layout-based visibility.
   */
  private loadColumnSettings() {
    try {
      const stored = localStorage.getItem(COLUMN_SETTINGS_STORAGE_KEY);

      // if no local settings, try API default or layout-based
      if (!stored) {
        if (this.gridSettingsDefault) {
          this.applySettings(this.gridSettingsDefault);
        } else {
          this.applyLayoutBasedVisibility();
        }
        return;
      }

      const settings = JSON.parse(stored);
      this.applySettings(settings);
    } catch (e) {
      console.error('Failed to load column settings', e);
    }
  }

  /**
   * Applies settings (visibility, order, sort) to the grid.
   */
  private applySettings(settings: {visibility?: {[key: string]: boolean}, order?: string[], sort?: string, sortOrder?: 'asc' | 'desc'}) {
    // apply visibility
    if (settings.visibility) {
      this.columns.forEach(col => {
        if (settings.visibility.hasOwnProperty(col.key)) {
          col.visible = settings.visibility[col.key];
        }
      });
    }

    // apply order (only for non-footer columns)
    if (settings.order && settings.order.length > 0) {
      const reordered: GridColumn[] = [];
      // first add columns in saved order
      settings.order.forEach((key: string) => {
        const col = this.columns.find(c => c.key === key);
        if (col && !this.isFooterColumn(col)) {
          reordered.push(col);
        }
      });
      // add any new columns not in saved order (before footer)
      this.columns.forEach(col => {
        if (!this.isFooterColumn(col) && reordered.indexOf(col) === -1) {
          reordered.push(col);
        }
      });
      // add footer columns at the end
      this.columns.forEach(col => {
        if (this.isFooterColumn(col)) {
          reordered.push(col);
        }
      });
      this.columns = reordered;
    }

    // apply sort
    if (settings.sort) {
      this.sort = settings.sort;
    }
    if (settings.sortOrder) {
      this.order = settings.sortOrder;
    }
  }

  /**
   * Applies visibility based on master data layout configuration.
   * Hides columns for fields not present in the layout.
   */
  private applyLayoutBasedVisibility() {
    // if no layout is configured, do nothing
    if (!this.masterDataLayout || !this.masterDataLayout.positions || this.masterDataLayout.positions.length === 0) {
      return;
    }

    const layoutFieldKeys = this.masterDataLayout.positions
      .filter(p => p.fieldType === 'standard' || p.fieldType === 'custom')
      .map(p => p.fieldKey);

    this.columns.forEach(col => {
      // skip footer columns (metadata)
      if (this.isFooterColumn(col)) {
        return;
      }

      // handle custom field columns
      if (col.isCustom) {
        const customKey = col.key.replace('custom.', '');
        col.visible = layoutFieldKeys.includes(customKey);
        return;
      }

      // handle standard field columns with special mappings
      switch (col.key) {
        case 'uid':
          // uid is always visible (internal identifier)
          break;
        case 'name':
          // name column is visible if firstName OR lastName is in layout
          col.visible = layoutFieldKeys.includes('firstName') || layoutFieldKeys.includes('lastName');
          break;
        case 'address':
          // address column is visible if any address-related field is in layout
          col.visible = layoutFieldKeys.includes('address') ||
                        layoutFieldKeys.includes('street') ||
                        layoutFieldKeys.includes('streetNr') ||
                        layoutFieldKeys.includes('zipCode') ||
                        layoutFieldKeys.includes('city');
          break;
        case 'location':
          col.visible = layoutFieldKeys.includes('location');
          break;
        case 'customerNr':
          col.visible = layoutFieldKeys.includes('customerNr');
          break;
        case 'email':
          col.visible = layoutFieldKeys.includes('email');
          break;
        default:
          // for any other standard columns, check direct key match
          col.visible = layoutFieldKeys.includes(col.key);
      }
    });
  }

  /**
   * Returns patients which are allowed by currently set filter
   *
   * @returns {Array}
   */
  public getPatients(): Patient[] {
    return this.patients;
  }

  /**
   * Returns custom field value from patient safely.
   *
   * @param patient Patient object.
   * @param key Custom field key (without 'custom.' prefix).
   * @returns Field value or empty string if not found.
   */
  public getCustomValue(patient: Patient, key: string): any {
    if (!patient.custom || patient.custom[key] === undefined || patient.custom[key] === null) {
      return '';
    }
    return patient.custom[key];
  }

  /**
   * Returns the type of a custom field by key.
   *
   * @param key Custom field key.
   * @returns Field type or null if not found.
   */
  public getCustomFieldType(key: string): string | null {
    const def = this.customFieldDefinitions.find(d => d.key === key);
    return def ? def.type : null;
  }

  /**
   * Returns the CSS grid-template-columns value for the search grid.
   *
   * @returns CSS grid-template-columns value.
   */
  public getSearchGridTemplateColumns(): string {
    return this.masterDataLayoutService.getGridTemplateColumns(this.effectiveLayout);
  }

  /**
   * Returns the CSS grid-column style for a field position.
   *
   * @param position Field position.
   * @returns CSS grid-column value.
   */
  public getSearchGridColumn(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridColumn(position);
  }

  /**
   * Returns the CSS grid-row style for a field position.
   *
   * @param position Field position.
   * @returns CSS grid-row value.
   */
  public getSearchGridRow(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridRow(position);
  }

  /**
   * Returns the label for a search field based on its position.
   *
   * @param position Field position.
   * @returns Translated label string.
   */
  public getSearchFieldLabel(position: MasterDataFieldPosition): string {
    if (position.fieldType === 'custom') {
      const def = this.customFieldDefinitions.find(d => d.key === position.fieldKey);
      return def ? def.label : position.fieldKey;
    }
    // standard field - get label key from service
    const labelKey = this.masterDataLayoutService.getStandardFieldLabelKey(position.fieldKey);
    return this._(KEYS.CRUD[labelKey]) || position.fieldKey;
  }

  /**
   * Returns the custom field definition for a given key.
   *
   * @param key Custom field key.
   * @returns CustomFieldDefinition or undefined.
   */
  public getCustomFieldDefinition(key: string): CustomFieldDefinition | undefined {
    return this.customFieldDefinitions.find(d => d.key === key);
  }

  /**
   * Checks if a standard field should be rendered as dropdown.
   *
   * @param fieldKey The field key to check.
   * @returns true if the field is a dropdown field.
   */
  public isDropdownField(fieldKey: string): boolean {
    const dropdownFields = ['gender', 'allowMail', 'prefill', 'locale'];
    return dropdownFields.includes(fieldKey);
  }

  /**
   * Returns object keys for iteration.
   *
   * @param obj Object to get keys from.
   * @returns Array of keys.
   */
  public objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  /**
   * Translates a language name.
   *
   * @param language Language name to translate.
   * @returns Translated language name.
   */
  public translateLanguage(language: string): string {
    return this.translationService ?
      this.translationService.translate({
        text: language,
        scope: ['cloudTranslatorModule', 'default']
      }) : language;
  }

  /**
   * Checks if custom field value is truthy (for boolean fields).
   *
   * @param patient Patient object.
   * @param key Custom field key.
   * @returns true if value is truthy, false otherwise.
   */
  public isCustomValueTrue(patient: Patient, key: string): boolean {
    if (!patient.custom) {
      return false;
    }
    return patient.custom[key] === true || patient.custom[key] === 'true' || patient.custom[key] === 1;
  }

  /**
   * Returns column label (handles custom fields).
   *
   * @param col Column configuration.
   * @returns Translated label or custom label.
   */
  public getColumnLabel(col: GridColumn): string {
    if (col.isCustom && col.customLabel) {
      return col.customLabel;
    }
    return this._(KEYS.GRID[col.labelKey]);
  }

  private loadPatients() {
    this.showPreloader();

    const filter = this.getQueryFilter();
    const opts = this.getQueryOpts();

    this.patientService.loadPatients(filter, opts)
      .then((result) => {
        this.patients = result.rows;
        this.total = result.total;
        this.hidePreloader();
      })
      .catch(error => {
        this.hidePreloader();
        alert(this._(KEYS.GRID.ERROR_LOADING_PATIENT));
        console.log(error);
      });
  }

  /**
   * Returns query filter by filter object.
   */
  private getQueryFilter() {
    const filter: any = {};

    // fields that use exact match instead of regex
    const exactMatchFields = ['gender', 'locale', 'prefill'];
    const booleanFields = ['allowMail'];

    // process dynamic standard field filters
    Object.keys(this.filter).forEach((key) => {
      const value = (<any>this.filter)[key];
      if (!value) {
        return;
      }
      // search in all address-related fields using $or
      if (key === 'address') {
        filter.$or = [
          {address: {$regex: value, $options: 'i'}},
          {street: {$regex: value, $options: 'i'}},
          {streetNr: {$regex: value, $options: 'i'}},
          {zipCode: {$regex: value, $options: 'i'}},
          {city: {$regex: value, $options: 'i'}}
        ];
        return;
      }
      // boolean fields - convert string to boolean
      if (booleanFields.includes(key)) {
        filter[key] = value === 'true';
        return;
      }
      // exact match fields (dropdowns)
      if (exactMatchFields.includes(key)) {
        filter[key] = value;
        return;
      }
      // normal fields - regex search
      filter[key] = {'$regex': value, '$options': 'i'};
    });

    // process custom field filters
    Object.keys(this.customFilter).forEach((key) => {
      if (this.customFilter[key]) {
        const def = this.customFieldDefinitions.find(d => d.key === key);
        // use exact match for dropdowns, regex for text fields
        if (def && def.type === 'select') {
          filter[`custom.${key}`] = this.customFilter[key];
        } else {
          filter[`custom.${key}`] = {'$regex': this.customFilter[key], '$options': 'i'};
        }
      }
    });

    // process meta filter: type (anonymous/pseudonymous)
    if (this.metaFilter.type === '1') {
      // not anonymous / not pseudonymous
      filter.anonymous = {'$ne': 1};
      filter.pseudonymous = {'$ne': 1};
    } else if (this.metaFilter.type === '2') {
      // anonymous only
      filter.anonymous = 1;
    } else if (this.metaFilter.type === '3') {
      // pseudonymous only
      filter.pseudonymous = 1;
    }

    // process meta filter: uid (_id in database, uid is virtual)
    if (this.metaFilter.uid) {
      filter._id = {'$regex': `^${this.metaFilter.uid}`, '$options': 'i'};
    }

    return filter;
  }

  /**
   * Returns query opts including sort parameters.
   */
  private getQueryOpts() {
    const opts: any = {
      limit: this.displayPerPage,
      offset: (this.gridPageNumber - 1) * this.displayPerPage
    };

    if (this.sort) {
      opts.sort = this.order === 'asc' ? this.sort : '-' + this.sort;
    }

    return opts;
  }

  /**
   * Shows preloader after 200ms
   */
  private showPreloader() {
    this.showPreloaderTimeout = setTimeout(() => this.layoutService.showPreloader(), 200);
  }

  /**
   * Hides preloader
   */
  private hidePreloader() {
    clearTimeout(this.showPreloaderTimeout);
    this.layoutService.hidePreloader();
  }
}
